import axios, { AxiosError } from "axios";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { getAuth } from "firebase/auth";
import { Business } from "../slices/businessSlice"; // adjust import path
import { Timestamp } from "firebase/firestore";
import { BusinessDashboardThemeResponse } from "../slices/themeSlice";

const SERVER_URL = "https://us-central1-elisyanwealth.cloudfunctions.net/";
const BASE_URL = "http://127.0.0.1:5001/elisyanwealth/us-central1/";

// Business CRUD (list / create / update / getById) is migrated to the v2
// endpoints (functions/v2/endpoints/businessEndpoints.ts → deployed names
// listBusinesses_v2 / createBusiness_v2 / updateBusiness_v2 /
// getBusinessById_v2). They read/write the `businesses_v2` collection.
//
// Currently pointed at BASE_URL (functions emulator) for pre-deploy testing.
// After `firebase deploy --only functions:listBusinesses_v2,createBusiness_v2,updateBusiness_v2,getBusinessById_v2`
// flip this to SERVER_URL.
//
// NOT migrated (still SERVER_URL / legacy): getMonthlyBusinessReport.
const BUSINESS_V2_DEPLOYED = true;
const BUSINESS_V2_URL = BUSINESS_V2_DEPLOYED ? SERVER_URL : BASE_URL;

// White-label theme + asset domain — wired to the v2 `_v2` endpoints
// (functions/v2/endpoints/businessThemeEndpoints.ts →
// uploadBusinessTheme_v2 / uploadBusinessAsset_v2 /
// uploadAdminDashboardTheme_v2 / getAdminDashboardTheme_v2). They write
// businesses_v2 / businessTheme_v2 / businessThemeArchive_v2; the external
// side effects (companies-API publish, WebDAV asset write) still fire for
// real via serviceFactory. getAdminDashboardTheme_v2 is PUBLIC (app-mount
// theming, matches v1). Set THEME_V2_DEPLOYED = true after
// `firebase deploy --only functions:uploadBusinessTheme_v2,uploadBusinessAsset_v2,uploadAdminDashboardTheme_v2,getAdminDashboardTheme_v2`.
const THEME_V2_DEPLOYED = true;
const THEME_V2_URL = THEME_V2_DEPLOYED ? SERVER_URL : BASE_URL;

/** ------------------- HELPER ------------------- **/

async function getAuthToken(thunkAPI: any): Promise<string | null> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    thunkAPI.rejectWithValue("User not authenticated");
    return null;
  }

  return await user.getIdToken();
}

/** ------------------- ASYNC ACTIONS ------------------- **/

// Fetch all businesses (paginated)
export const fetchBusinesses = createAsyncThunk<
  {
    data: Business[];
    page: number;
    limit: number;
    total: number;
    lastDocId: string | null;
  },
  {
    page?: number;
    limit?: number;
    search?: string;
  },
  { rejectValue: string }
>(
  "businesses/fetchBusinesses",
  async ({ page = 1, limit = 100, search }, thunkAPI) => {
    try {
      const token = await getAuthToken(thunkAPI);
      if (!token) return thunkAPI.rejectWithValue("User not authenticated");

      const params: Record<string, any> = { page, limit };
      if (search) params.search = search;

      const response = await axios.get(`${BUSINESS_V2_URL}listBusinesses_v2`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("Fetched businesses:", response.data);
      return response.data;
    } catch (e) {
      console.error("Error fetching businesses:", e);
      if (e instanceof AxiosError && e.response?.data?.message) {
        return thunkAPI.rejectWithValue(e.response.data.message);
      }
      return thunkAPI.rejectWithValue(
        "Unexpected error occurred while fetching businesses."
      );
    }
  }
);

// Create a new business
export const createBusiness = createAsyncThunk<
  Business,
  {
    name: string;
    type: string;
    enable_advisors?: boolean;
    need_client_id?: boolean;
    need_admin_dashboard?: boolean;
    admins?: Array<{ email: string; roles: string[] }>;
  },
  { rejectValue: string }
>("businesses/createBusiness", async (businessData, thunkAPI) => {
  try {
    const token = await getAuthToken(thunkAPI);
    if (!token) return thunkAPI.rejectWithValue("User not authenticated");

    const response = await axios.post(
      `${BUSINESS_V2_URL}createBusiness_v2`,
      businessData,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    console.log("Business created:", response.data);
    return response.data.data;
  } catch (e) {
    console.error("Error creating business:", e);
    if (e instanceof AxiosError && e.response?.data?.message) {
      return thunkAPI.rejectWithValue(e.response.data.message);
    }
    return thunkAPI.rejectWithValue(
      "Unexpected error occurred while creating business."
    );
  }
});

// Update a business
export const updateBusiness = createAsyncThunk<
  Business,
  {
    id: string;
    updates: Partial<Omit<Business, "id" | "code" | "created_at">>;
  },
  { rejectValue: string }
>("businesses/updateBusiness", async ({ id, updates }, thunkAPI) => {
  try {
    const token = await getAuthToken(thunkAPI);
    if (!token) return thunkAPI.rejectWithValue("User not authenticated");

    const response = await axios.put(
      `${BUSINESS_V2_URL}updateBusiness_v2`,
      { id, ...updates },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    console.log("Business updated:", response.data);
    return response.data.data;
  } catch (e) {
    console.error("Error updating business:", e);
    if (e instanceof AxiosError && e.response?.data?.message) {
      return thunkAPI.rejectWithValue(e.response.data.message);
    }
    return thunkAPI.rejectWithValue(
      "Unexpected error occurred while updating business."
    );
  }
});

// Delete a business
export const deleteBusiness = createAsyncThunk<
  string,
  { id: string },
  { rejectValue: string }
>("businesses/deleteBusiness", async ({ id }, thunkAPI) => {
  try {
    const token = await getAuthToken(thunkAPI);
    if (!token) return thunkAPI.rejectWithValue("User not authenticated");

    await axios.delete(`${SERVER_URL}deleteBusiness`, {
      params: { id },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    console.log("Business deleted:", id);
    return id;
  } catch (e) {
    console.error("Error deleting business:", e);
    if (e instanceof AxiosError && e.response?.data?.message) {
      return thunkAPI.rejectWithValue(e.response.data.message);
    }
    return thunkAPI.rejectWithValue(
      "Unexpected error occurred while deleting business."
    );
  }
});

// Fetch single business by ID
export const fetchBusinessById = createAsyncThunk<
  Business,
  { id: string },
  { rejectValue: string }
>("businesses/fetchBusinessById", async ({ id }, thunkAPI) => {
  try {
    const token = await getAuthToken(thunkAPI);
    if (!token) return thunkAPI.rejectWithValue("User not authenticated");

    const response = await axios.get(`${BUSINESS_V2_URL}getBusinessById_v2`, {
      params: { id },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    console.log("Fetched business:", response.data);
    return response.data.data;
  } catch (e) {
    console.error("Error fetching business:", e);
    if (e instanceof AxiosError && e.response?.data?.message) {
      return thunkAPI.rejectWithValue(e.response.data.message);
    }
    return thunkAPI.rejectWithValue(
      "Unexpected error occurred while fetching business."
    );
  }
});

// Upload business theme
export const uploadBusinessTheme = createAsyncThunk<
  { clientCode: string; message: string; externalApiResponse?: any; businessData?: Business },
  {
    clientCode: string;
    jsonTheme: string;
  },
  { rejectValue: string }
>("businesses/uploadBusinessTheme", async ({ clientCode, jsonTheme }, thunkAPI) => {
  try {
    const token = await getAuthToken(thunkAPI);
    if (!token) return thunkAPI.rejectWithValue("User not authenticated");

    // Parse JSON to validate and send as object
    let themeData;
    try {
      themeData = JSON.parse(jsonTheme);
    } catch {
      return thunkAPI.rejectWithValue("Invalid JSON format");
    }

    const response = await axios.post(
      `${THEME_V2_URL}uploadBusinessTheme_v2`,
      {
        clientCode,
        theme: themeData,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    console.log("Theme uploaded:", response.data);
    return response.data;
  } catch (e) {
    console.error("Error uploading theme:", e);
    if (e instanceof AxiosError && e.response?.data?.message) {
      return thunkAPI.rejectWithValue(e.response.data.message);
    }
    return thunkAPI.rejectWithValue(
      "Unexpected error occurred while uploading theme."
    );
  }
});


// businessActions.ts - Single unified upload action
export const uploadBusinessAsset = createAsyncThunk<
{ 
  clientCode: string;
        fileType: string;
        publicUrl: string;
        storedPath: string;
        businessUpdated: boolean;
        timestamp: string;
}, { 
  clientCode: string; 
  fileType: 'logo' | 'theme' | 'background'; 
  fileName: string; 
  fileData: string;
}, { rejectValue: string }
>(
  'business/uploadAsset',
  async ({ 
    clientCode, 
    fileType, 
    fileName, 
    fileData 
  }, thunkAPI) => {
    try {
      const token = await getAuthToken(thunkAPI);
    if (!token) return thunkAPI.rejectWithValue("User not authenticated");
    // If it's just base64, add the prefix
    let formattedFileData = fileData;
    if (!fileData.startsWith('data:')) {
      // Detect file type from extension
      const ext = fileName.split('.').pop()?.toLowerCase();
      let mimeType = 'application/octet-stream';
      
      if (ext === 'svg') mimeType = 'image/svg+xml';
      else if (ext === 'png') mimeType = 'image/png';
      else if (ext === 'jpg' || ext === 'jpeg') mimeType = 'image/jpeg';
      else if (ext === 'json') mimeType = 'application/json';
      
      formattedFileData = `data:${mimeType};base64,${fileData}`;
    }
      const response = await axios.post(
        `${THEME_V2_URL}uploadBusinessAsset_v2`,
        {
          clientCode,
          fileType,
          fileName,
          fileData: formattedFileData,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || `Failed to upload ${fileType}`);
    }
  }
);

// businessActions.ts - Single unified upload action
export const uploadAppBusinessAsset = createAsyncThunk<
{ 
  clientCode: string;
        fileType: string;
        publicUrl: string;
        storedPath: string;
        businessUpdated: boolean;
        timestamp: string;
}, { 
  clientCode: string; 
  fileType: 'logos' | 'onboarding' | 'backgrounds'; 
  fileName: string; 
  fileData: string;
}, { rejectValue: string }
>(
  'business/uploadAppBusinessAsset',
  async ({ 
    clientCode, 
    fileType, 
    fileName, 
    fileData 
  }, thunkAPI) => {
    try {
      const token = await getAuthToken(thunkAPI);
    if (!token) return thunkAPI.rejectWithValue("User not authenticated");
    // If it's just base64, add the prefix
    let formattedFileData = fileData;
    if (!fileData.startsWith('data:')) {
      // Detect file type from extension
      const ext = fileName.split('.').pop()?.toLowerCase();
      let mimeType = 'application/octet-stream';
      
      if (ext === 'svg') mimeType = 'image/svg+xml';
      else if (ext === 'png') mimeType = 'image/png';
      else if (ext === 'jpg' || ext === 'jpeg') mimeType = 'image/jpeg';
      else if (ext === 'json') mimeType = 'application/json';
      
      formattedFileData = `data:${mimeType};base64,${fileData}`;
    }
      const response = await axios.post(
        `${THEME_V2_URL}uploadBusinessAsset_v2`,
        {
          clientCode,
          fileType,
          fileName,
          fileData: formattedFileData,
          appAssets: true, // Flag to indicate these are app-level assets
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || `Failed to upload ${fileType}`);
    }
  }
);
// ============== UPLOAD DASHBOARD THEME ==============
export const uploadDashboardTheme = createAsyncThunk<
  {
    success: boolean;
    message: string;
    data: {
      businessId: string;
      clientCode: string;
      businessName?: string;
      themeVersion: number;
      themeDocId: string;
      logoUrl?: string | null;
      businessData?: Business;
      themePreview?: {
        colors?: Record<string, string>;
        fonts?: Record<string, string>;
        version: number;
      };
    };
  },
  {
    clientCode: string;
    theme: Record<string, any>; // Accepts theme object directly
  },
  { rejectValue: string }
>("businessTheme/uploadDashboardTheme", async ({ clientCode, theme }, thunkAPI) => {
  try {
    const token = await getAuthToken(thunkAPI);
    if (!token) return thunkAPI.rejectWithValue("User not authenticated");

    const response = await axios.post(
      `${THEME_V2_URL}uploadAdminDashboardTheme_v2`,
      {
        clientCode,
        theme,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    console.log("✅ Dashboard theme uploaded:", response.data);
    return response.data;
  } catch (e) {
    console.error("❌ Error uploading dashboard theme:", e);
    if (e instanceof AxiosError && e.response?.data?.message) {
      return thunkAPI.rejectWithValue(e.response.data.message);
    }
    return thunkAPI.rejectWithValue(
      "Unexpected error occurred while uploading dashboard theme."
    );
  }
});

// ============== UPLOAD DASHBOARD THEME FROM JSON STRING ==============
// Convenience wrapper that accepts JSON string (for backward compatibility)
export const uploadDashboardThemeFromJson = createAsyncThunk<
  {
    success: boolean;
    message: string;
    data: {
      businessId: string;
      clientCode: string;
      businessName?: string;
      themeVersion: number;
      themeDocId: string;
      logoUrl?: string | null;
      businessData?: Business;
    };
  },
  {
    clientCode: string;
    jsonTheme: string;
  },
  { rejectValue: string }
>("businessTheme/uploadDashboardThemeFromJson", async ({ clientCode, jsonTheme }, thunkAPI) => {
  try {
    const token = await getAuthToken(thunkAPI);
    if (!token) return thunkAPI.rejectWithValue("User not authenticated");

    // Parse JSON to validate
    let themeData;
    try {
      themeData = JSON.parse(jsonTheme);
    } catch {
      return thunkAPI.rejectWithValue("Invalid JSON format");
    }

    // Reuse the uploadDashboardTheme action
    return thunkAPI.dispatch(
      uploadDashboardTheme({ clientCode, theme: themeData })
    ).unwrap();
  } catch (e) {
    console.error("❌ Error uploading dashboard theme from JSON:", e);
    if (e instanceof AxiosError && e.response?.data?.message) {
      return thunkAPI.rejectWithValue(e.response.data.message);
    }
    return thunkAPI.rejectWithValue(
      "Unexpected error occurred while uploading dashboard theme."
    );
  }
});

// ============== GET DASHBOARD THEME ==============
export const getDashboardTheme = createAsyncThunk<
  {
    success: boolean;
    data: BusinessDashboardThemeResponse;
  },
  {
    businessId?: string;
    clientCode?: string;
    version?: number;
  },
  { rejectValue: string }
>("theme/getDashboardTheme", async ({ businessId, clientCode, version }, thunkAPI) => {
  try {
    console.log("🔍 Fetching dashboard theme with params:", { businessId, clientCode, version });
    const params: Record<string, string | number> = {};
    if (businessId) params.businessId = businessId;
    if (clientCode) params.clientCode = clientCode;
    if (version) params.version = version;

    const response = await axios.get(
      `${THEME_V2_URL}getAdminDashboardTheme_v2`,
      {
        headers: {
          Authorization: `Bearer `,
        },
        params,
      }
    );

    console.log("✅ Dashboard theme fetched:", response.data);
    return response.data;
  } catch (e) {
    console.error("❌ Error fetching dashboard theme:", e);
    
    // Special handling for 404 - theme not found
    if (e instanceof AxiosError && e.response?.status === 404) {
      return thunkAPI.rejectWithValue("THEME_NOT_FOUND");
    }
    
    if (e instanceof AxiosError && e.response?.data?.message) {
      return thunkAPI.rejectWithValue(e.response.data.message);
    }
    return thunkAPI.rejectWithValue(
      "Unexpected error occurred while fetching dashboard theme."
    );
  }
});

// ============== GET DASHBOARD THEME ==============
export const getSelectedBusinessDashboardTheme = createAsyncThunk<
  {
    success: boolean;
    data: BusinessDashboardThemeResponse;
  },
  {
    businessId?: string;
    clientCode?: string;
    version?: number;
  },
  { rejectValue: string }
>("businesses/getSelectedBusinessDashboardTheme", async ({ businessId, clientCode, version }, thunkAPI) => {
  try {
    console.log("🔍 Fetching dashboard theme with params:", { businessId, clientCode, version });
    const params: Record<string, string | number> = {};
    if (businessId) params.businessId = businessId;
    if (clientCode) params.clientCode = clientCode;
    if (version) params.version = version;

    const response = await axios.get(
      `${THEME_V2_URL}getAdminDashboardTheme_v2`,
      {
        headers: {
          Authorization: `Bearer `,
        },
        params,
      }
    );

    console.log("✅ Dashboard theme fetched:", response.data);
    return response.data;
  } catch (e) {
    console.error("❌ Error fetching dashboard theme:", e);
    
    // Special handling for 404 - theme not found
    if (e instanceof AxiosError && e.response?.status === 404) {
      return thunkAPI.rejectWithValue("THEME_NOT_FOUND");
    }
    
    if (e instanceof AxiosError && e.response?.data?.message) {
      return thunkAPI.rejectWithValue(e.response.data.message);
    }
    return thunkAPI.rejectWithValue(
      "Unexpected error occurred while fetching dashboard theme."
    );
  }
});



export const getThemeConfigFile = async (code: string): Promise<ConfigResponse | false> => {
  try {
    const res = await fetch(
      `https://b14h7isg51.execute-api.eu-west-2.amazonaws.com/staging/companies/byCode?clientCode=${code?.toUpperCase()}&v=2`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    const d = await res.json();
    if (res.status > 299) {
      console.log('status: ' + res.status);
      throw { errorMessage: d.errorMessage, error: d };
    }
    if (d.response.data[0]) {
      //delete d.response.data[0].config; // remove config from response to replicate white screen issue
      return d.response.data[0] as ConfigResponse;
    } else {
      return false;
    }
  } catch (e) {
    console.log('Error While Fetch Config theme file ' + JSON.stringify(e));
    throw e;
  }
};



//////////////////////////
/// Theme Config File ///
////////////////////////
export interface ConfigResponse {
  clientCode: string;
  id: string;
  clientName: string;
  needClientId?: boolean;
  config: Config;
}
export interface ColorSchema {
  charts: ConfigCharts;
  colors: ConfigColors;
  buttons: ConfigButtons;
  icons: ConfigIcons; 
}

export interface ConfigIcons {
  navbar: {
    focused : {
      light: string;
      dark: string;
    },
    unfocused : {
      light: string;
      dark: string;
    }
  }
}

export interface Config {
  onBoardingMessages: OnBoardingMessage[];
  name: string;
  colorSchema: ColorSchema;
  needClientId?: boolean;
  images: ConfigImages;
  texts: ConfigTexts;
  textsFr: ConfigTexts;
}

export interface ConfigTexts {
  welcomeScreen: string;
  signupScreen: string;
  loginScreen: string;
  loginScreenRegister: string;
  welcomeModal: string;
  clientIdIdentifier?: string;
}

export interface ConfigButtons {
  secondary: ConfigButtonVariant;
  plus: ConfigButtonVariant;
  primary: ConfigButtonVariant;
}

export interface ImageSchemaMode {
  dark?: string;
  light: string;
  style?: {
    width?: number;
    height?: number;
    size?: number;
  };
}
export interface ColorSchemaMode {
  dark?: string[];
  light: string[];
}

export interface ConfigButtonVariant {
  icon?: ColorSchemaMode;
  outline?: ColorSchemaMode;
  text: ColorSchemaMode;
  background: ColorSchemaMode;
}

export interface ConfigCharts {
  assetsAndLiabilitiesBar: ColorSchemaMode;
  historyChartLine: ColorSchemaMode;
  netWorthBar: ColorSchemaMode;
  pieChart: PieChart;
  historyChartArea: ColorSchemaMode;
}

export interface KeysPair {
  publicKey: string;
  privateKey: string;
}

export interface PieChart {
  randomColors: ColorSchemaMode[];
  categories: ConfigFileCategories;
  countries: ConfigFileCountries;
  currencies: ConfigFileCurrencies;
  geoMonitor: ConfigFileGetMonitor;
}

export interface ConfigFileGetMonitor {
  work: ColorSchemaMode;
  leisure: ColorSchemaMode;
  transit: ColorSchemaMode;
  other: ColorSchemaMode;
}

export interface ConfigFileCurrencies {
  USD: ColorSchemaMode;
  EUR: ColorSchemaMode;
  GBP: ColorSchemaMode;
  AED: ColorSchemaMode;
  CAD: ColorSchemaMode;
  HUF: ColorSchemaMode;
  SGD: ColorSchemaMode;
  MAD: ColorSchemaMode;
  CHF: ColorSchemaMode;
  AUD: ColorSchemaMode;
}

export interface ConfigFileCategories {
  cash: ColorSchemaMode;
  investment: ColorSchemaMode;
  retirement: ColorSchemaMode;
  realEstate: ColorSchemaMode;
  digital: ColorSchemaMode;
  insurance: ColorSchemaMode;
  other: ColorSchemaMode;
  collectibles: ColorSchemaMode;
  creditCard: ColorSchemaMode;
  mortgage: ColorSchemaMode;
  loan: ColorSchemaMode;
}

export interface ConfigFileImageCategories {
  cash: ImageSchemaMode;
  investment: ImageSchemaMode;
  retirement: ImageSchemaMode;
  realEstate: ImageSchemaMode;
  digital: ImageSchemaMode;
  insurance: ImageSchemaMode;
  other: ImageSchemaMode;
  collectibles: ImageSchemaMode;
  creditCard: ImageSchemaMode;
  mortgage: ImageSchemaMode;
  loan: ImageSchemaMode;
}

export interface ConfigFileCountries {
  BE: ColorSchemaMode;
  AE: ColorSchemaMode;
  PT: ColorSchemaMode;
  CH: ColorSchemaMode;
  GB: ColorSchemaMode;
  LU: ColorSchemaMode;
  FR: ColorSchemaMode;
  HU: ColorSchemaMode;
  AT: ColorSchemaMode;
  SG: ColorSchemaMode;
  MA: ColorSchemaMode;
  AU: ColorSchemaMode;
  US: ColorSchemaMode;
  CA: ColorSchemaMode;
}

export interface ConfigColors {
  secondary: ColorSchemaMode;
  progressBarDashboard: ColorSchemaMode;
  secondaryBackground: ColorSchemaMode;
  mainBackground: ColorSchemaMode;
  profilePictureStroke: ColorSchemaMode;
  mainText: ColorSchemaMode;
  primary: ColorSchemaMode;
  secondaryText: ColorSchemaMode;
}

export interface ConfigImages {
  iconsCategories: ConfigFileImageCategories;
  iconsCategoriesGold: ConfigFileImageCategories;
  navigationBarIcons: NavigationBarIcons;
  settingsIcons: ImageSchemaMode[];
  logo: {
    primary: ImageSchemaMode;
    secondary: ImageSchemaMode;
  };
  background: { dashboard: ImageSchemaMode };
  icons: {
    eyeOpen: ImageSchemaMode;
    eyeClose: ImageSchemaMode;
    world: ImageSchemaMode;
    plus: ImageSchemaMode;
    faceId: ImageSchemaMode;
    fingerPrint: ImageSchemaMode;
    arrowDownFilled: ImageSchemaMode;
    arrowUpFilled: ImageSchemaMode;
    arrowDown: ImageSchemaMode;
    arrowUp: ImageSchemaMode;
    arrowLeft: ImageSchemaMode;
    arrowRight: ImageSchemaMode;
    camera: ImageSchemaMode;
    checkboxClear: ImageSchemaMode;
    checkboxTicked: ImageSchemaMode;
    crossFiled: ImageSchemaMode;
    cross: ImageSchemaMode;
    images: ImageSchemaMode;
    linked: ImageSchemaMode;
    plusFiled: ImageSchemaMode;
    plusIcon: ImageSchemaMode;
    trash: ImageSchemaMode;
    iconAdmin: ImageSchemaMode;
    iconManageConnections: ImageSchemaMode;
    iconManageData: ImageSchemaMode;
    iconReConsentTicked: ImageSchemaMode;
    iconSyncError: ImageSchemaMode;
    iconSyncTicked: ImageSchemaMode;
    iconToggleActive: ImageSchemaMode;
    iconToggleInactive: ImageSchemaMode;
    iconLockTicked: ImageSchemaMode;
    iconSyncAnimated: ImageSchemaMode;
    iconBell: ImageSchemaMode;
    iconInfo: ImageSchemaMode;
    iconHelp: ImageSchemaMode;
    iconShare: ImageSchemaMode;
    iconLocation: ImageSchemaMode;
    iconEdit: ImageSchemaMode;
    iconReconsent: ImageSchemaMode;
    iconTryingReconnect: ImageSchemaMode;
  };
  onboardingHeaderImages: ImageSchemaMode[];
  dashboardHeader: ImageSchemaMode;
}

export interface NavigationBarIcons {
  analytics: SelectedMode;
  settings: SelectedMode;
  accounts: SelectedMode;
  dashboard: SelectedMode;
}

export interface SelectedMode {
  active: ImageSchemaMode;
  inactive: ImageSchemaMode;
}

export interface OnBoardingMessage {
  title: string;
  body: string;
}
//////////////////////////
